import { useMemo, useState } from 'react';
import {
  EXTERNAL_SYSTEMS,
  EXTERNAL_SYSTEM_META,
  LEASE_MINUTES_DEFAULT,
  LEASE_MINUTES_MAX,
  LEASE_MINUTES_MIN,
} from '@shared/external-writes/systems';
import type { ExternalSystem } from '@shared/external-writes/systems';
import type { ExternalWritesState, SystemLease } from '@shared/external-writes/wire';
import { describeRow } from '@shared/external-writes/describe';
import { AUTOFILL_IGNORE_PROPS } from '../../lib/autofill';
import { useExternalWrites } from './useExternalWrites';

const MIN_LIVE_REASON = 3;

function leaseFor(state: ExternalWritesState | 'unknown', system: ExternalSystem): SystemLease | undefined {
  return state === 'unknown' ? undefined : state.systems.find((l) => l.system === system);
}

type Pending = { kind: 'live'; systems: ExternalSystem[] } | { kind: 'sandbox'; systems: ExternalSystem[] };

export function ExternalWritesPopover({ onClose }: { onClose: () => void }) {
  const { state, lease, revoke } = useExternalWrites({ sync: false });
  const [selected, setSelected] = useState<ReadonlySet<ExternalSystem>>(new Set());
  const [minutes, setMinutes] = useState(LEASE_MINUTES_DEFAULT);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const now = new Date();
  const ceilinged = useMemo(
    () => new Set(EXTERNAL_SYSTEMS.filter((s) => leaseFor(state, s)?.ceiling === true)),
    [state],
  );
  const leasable = EXTERNAL_SYSTEMS.filter((s) => !ceilinged.has(s));
  const trimmedReason = reason.trim();
  const reasonOk = trimmedReason.length >= MIN_LIVE_REASON;
  const clampedMinutes = Math.min(Math.max(Math.round(minutes) || LEASE_MINUTES_DEFAULT, LEASE_MINUTES_MIN), LEASE_MINUTES_MAX);

  const toggle = (system: ExternalSystem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(system)) next.delete(system);
      else next.add(system);
      return next;
    });
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'live') await lease(pending.systems, clampedMinutes, trimmedReason);
      else await revoke(pending.systems, trimmedReason);
      onClose();
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const selectedList = leasable.filter((s) => selected.has(s));
  const inputClass =
    'w-full rounded border border-border-primary bg-bg-primary px-2 py-1 text-xs text-content-primary';
  const buttonClass =
    'rounded px-2.5 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div
      role="dialog"
      aria-label="External writes"
      className="fixed bottom-[130px] right-4 z-50 w-[360px] rounded-lg border border-border-primary bg-bg-primary p-3 text-xs text-content-primary shadow-lg"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">External writes</span>
        <button type="button" onClick={onClose} aria-label="Close external writes" className="text-content-faint hover:text-content-primary">
          &times;
        </button>
      </div>

      {pending ? (
        <div className="space-y-2" data-testid="external-writes-confirm">
          {pending.kind === 'live' ? (
            <>
              <p className="font-semibold">
                Go live for {clampedMinutes} min? Real writes will happen:
              </p>
              <ul className="list-disc space-y-1 pl-4">
                {pending.systems.map((s) => (
                  <li key={s}>
                    <span className="font-semibold">{EXTERNAL_SYSTEM_META[s].label}</span>: {EXTERNAL_SYSTEM_META[s].blastRadius}
                  </li>
                ))}
              </ul>
              {pending.systems.length < EXTERNAL_SYSTEMS.length && (
                <p className="text-content-faint">
                  Other systems stay sandboxed; a batch that also writes them will fail those items.
                </p>
              )}
            </>
          ) : (
            <p className="font-semibold">Sandbox {pending.systems.map((s) => EXTERNAL_SYSTEM_META[s].label).join(', ')}?</p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className={`${buttonClass} text-content-faint`} onClick={() => setPending(null)} disabled={busy}>
              Back
            </button>
            <button
              type="button"
              className={`${buttonClass} ${pending.kind === 'live' ? 'bg-bg-danger-inverse text-content-inverse' : 'bg-bg-success-inverse text-content-inverse'}`}
              onClick={() => { void confirm(); }}
              disabled={busy}
            >
              {pending.kind === 'live' ? 'Confirm live' : 'Confirm sandbox'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <ul className="space-y-1.5">
            {EXTERNAL_SYSTEMS.map((system) => {
              const isCeiling = ceilinged.has(system);
              return (
                <li key={system}>
                  <label className={`flex items-start gap-2 ${isCeiling ? 'opacity-60' : ''}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selected.has(system)}
                      disabled={isCeiling}
                      onChange={() => toggle(system)}
                      aria-label={EXTERNAL_SYSTEM_META[system].label}
                    />
                    <span>
                      <span className="font-semibold">{EXTERNAL_SYSTEM_META[system].label}</span>
                      <span className="block text-content-faint">{describeRow(leaseFor(state, system), now)}</span>
                      {isCeiling && (
                        <span className="block text-content-faint">Locked by TERRA_WRITES_CEILING for this environment</span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 grid grid-cols-[80px_1fr] items-center gap-2">
            <label htmlFor="ew-minutes">Minutes</label>
            <input
              {...AUTOFILL_IGNORE_PROPS}
              id="ew-minutes"
              type="number"
              min={LEASE_MINUTES_MIN}
              max={LEASE_MINUTES_MAX}
              className={inputClass}
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
            />
            <label htmlFor="ew-reason">Reason</label>
            <input
              {...AUTOFILL_IGNORE_PROPS}
              id="ew-reason"
              type="text"
              className={inputClass}
              value={reason}
              placeholder="Required (3+ chars for live)"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className={`${buttonClass} bg-bg-warning-inverse text-content-inverse`}
              disabled={selectedList.length === 0 || !reasonOk || state === 'unknown'}
              onClick={() => setPending({ kind: 'live', systems: selectedList })}
            >
              Lease selected
            </button>
            <button
              type="button"
              className={`${buttonClass} bg-bg-danger-inverse text-content-inverse`}
              disabled={leasable.length === 0 || !reasonOk || state === 'unknown'}
              onClick={() => setPending({ kind: 'live', systems: [...leasable] })}
            >
              All live
            </button>
            <button
              type="button"
              className={`${buttonClass} bg-bg-success-inverse text-content-inverse`}
              disabled={trimmedReason.length < 1 || state === 'unknown'}
              onClick={() => setPending({ kind: 'sandbox', systems: [...EXTERNAL_SYSTEMS] })}
            >
              All sandbox
            </button>
          </div>
        </>
      )}
    </div>
  );
}
